import React, { useContext, useEffect, createContext, ReactNode, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ReadyState } from 'react-use-websocket'

import { useQueryChargingStatus } from '@/api/charging/queries'

import { useAuth } from '@/hooks/useAuth'
import { useActiveChargingSession } from '@/hooks/useActiveChargingSession'
import { useInAppNotifications } from '@/hooks/useInAppNotifications'

import { InAppNotificationCategory, InAppNotificationData } from '@/lib/in-app-notifications/types'
import { ChargingSessionInAppNotificationPayload } from '@/lib/in-app-notifications/categories/charging_session'

import { AppRoutes } from '@/routes/types/routes'

import { useToast } from './ToastContext'
import { Logger } from '@/lib/logger'

const logger = new Logger('ActiveSessionContext')

interface ActiveSessionContextProps extends ReturnType<
  typeof useInAppNotifications<InAppNotificationCategory.CHARGING_SESSION>
> {
  notification: InAppNotificationData<ChargingSessionInAppNotificationPayload> | null
  dismissSession: () => Promise<void>
}

const ActiveSessionContext = createContext<ActiveSessionContextProps>({
  notification: null,
  dismissSession: async () => {},
  clearLiveNotification: () => {},
  connected: false,
  connectionState: ReadyState.CLOSED,
})

export const ActiveSessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const toast = useToast()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const params = useParams()
  const rawParamChargingPointId = Number(params.chargingPointId)
  const paramChargingPointId = isNaN(rawParamChargingPointId) ? undefined : rawParamChargingPointId

  const { activeSession: storedSession, updateActiveSession, clearActiveSession } = useActiveChargingSession()

  const activeChargingPointId = useMemo(() => {
    return paramChargingPointId || storedSession?.data.payload.charging_point_id
  }, [paramChargingPointId, storedSession])

  const {
    notification: liveSession,
    clearLiveNotification,
    ...inAppNotification
  } = useInAppNotifications(InAppNotificationCategory.CHARGING_SESSION, {
    enabled: isAuthenticated,
    authenticationPayload: activeChargingPointId ? { charging_point_id: activeChargingPointId } : undefined,
    urlParams: activeChargingPointId ? { cp: activeChargingPointId } : undefined,
    onError: (_, message) => {
      toast.error(message)
      clearActiveSession()
      navigate(AppRoutes.STATIONS)
    },
  })

  const { data: chargingStatus, isError } = useQueryChargingStatus(
    { charging_point_id: (paramChargingPointId || storedSession?.data.payload.charging_point_id)! },
    { enabled: isAuthenticated && (!!paramChargingPointId || !!storedSession?.data.payload.charging_point_id) }
  )

  useEffect(() => {
    if (isError) {
      clearActiveSession()
    }
  }, [isError])

  useEffect(() => {
    if (liveSession) {
      updateActiveSession(liveSession)
    }
  }, [liveSession, updateActiveSession])

  useEffect(() => {
    if (chargingStatus) {
      try {
        if (!storedSession) return

        if (!chargingStatus.id) {
          clearActiveSession()
          return
        }

        if (chargingStatus.id !== storedSession.data.payload.session_id) {
          clearActiveSession()
          return
        }

        updateActiveSession({
          ...storedSession,
          data: {
            ...storedSession.data,
            payload: {
              ...storedSession.data.payload,
              total_cost: chargingStatus.total_cost?.toString() || storedSession.data.payload.total_cost,
              kwh_delivered: chargingStatus.kwh_delivered ?? storedSession.data.payload.kwh_delivered,
              status: chargingStatus.status || storedSession.data.payload.status,
              session_id: chargingStatus.id || storedSession.data.payload.session_id,
            },
          },
        })
      } catch (_e) {
        logger.warn('Sessão não recuperada do backend, limpando storage.')
        clearActiveSession()
      }
    }
  }, [chargingStatus])

  const syncedNotification = useMemo((): InAppNotificationData<ChargingSessionInAppNotificationPayload> | null => {
    if (liveSession) return liveSession

    if (
      storedSession &&
      (!paramChargingPointId || storedSession.data.payload.charging_point_id === paramChargingPointId)
    ) {
      return storedSession
    }

    return null
  }, [liveSession, storedSession, paramChargingPointId])

  const dismissSession = useCallback(async () => {
    clearLiveNotification()
    await clearActiveSession()
  }, [clearActiveSession, clearLiveNotification])

  return (
    <ActiveSessionContext.Provider
      value={{
        ...inAppNotification,
        notification: syncedNotification,
        dismissSession,
        clearLiveNotification,
      }}
    >
      {children}
    </ActiveSessionContext.Provider>
  )
}

export const useActiveSession = () => {
  const context = useContext(ActiveSessionContext)

  if (context === undefined) {
    throw new Error('useActiveSession must be used within a ActiveSessionProvider')
  }

  return context
}
